import type { ChannelFilters, DefaultGenerics, EventTypes } from "stream-chat";

import { useCallback, useEffect, useState } from "react";

import { useStreamClient } from "@/context/stream-context";
import { useMe } from "@/context/user-context";
import { reportStreamChatError } from "@/lib/stream-chat-errors";

const listOfEvents: EventTypes[] = [
  "thread.updated",
  "notification.thread_message_new",
  "notification.removed_from_channel",
  "notification.added_to_channel",
  "notification.mark_read",
  "notification.mark_unread",
  "notification.message_new",
  "message.new",
  "message.read",
  "message.updated",
  "channel.created",
  "channel.deleted",
];

export const useHasUnreadMessages = () => {
  // Nullable volontairement : un club freemium n'a aucun client StreamChat, la
  // pastille de non-lus ne s'allume alors jamais.
  const client = useStreamClient();
  const { streamUser } = useMe();
  const [hasUnread, setHasUnread] = useState(false);

  const checkUnreadMessages = useCallback(async () => {
    if (!client) {
      return false;
    }

    try {
      const filter: ChannelFilters<DefaultGenerics> = { members: { $in: [streamUser.id] } };
      const channels = await client.queryChannels(filter);

      // Vérifier s'il y a au moins un canal avec des messages non lus
      const hasUnreadMessages = channels.some(channel => channel.state.unreadCount > 0);

      setHasUnread(hasUnreadMessages);
      return hasUnreadMessages;
    }
    catch (error) {
      reportStreamChatError(error, "check-unread-messages");
      setHasUnread(false);
      return false;
    }
  }, [client, streamUser]);

  useEffect(() => {
    if (!client) {
      return;
    }

    const unsubscribe = client.on("all", (event) => {
      if (listOfEvents.includes(event.type)) {
        void checkUnreadMessages();
      }
    });

    return () => {
      unsubscribe.unsubscribe();
    };
  }, [client, streamUser, checkUnreadMessages]);

  useEffect(() => {
    void checkUnreadMessages();
  }, [checkUnreadMessages]);

  return { hasUnread, checkUnreadMessages };
};
